<template>
  <div class="developer-information bg-background pb-5">
    <div
      class="mb-4 description d-flex justify-content-center text-center text-white align-items-center"
    >
      <h1>{{ hebrew ? "Dicta LM 3.0" : "Dicta LM 3.0" }}</h1>
    </div>
    <b-container dir="ltr" class="text-left">
      <div class="d-flex justify-content-around">
        <a href="https://dicta.org.il"
          ><img src="@/assets/llm/image1.jpg" class="logo-item w-auto"
        /></a>
        <a href="https://nnlp-il.mafat.ai"
          ><img src="@/assets/llm/image5.jpg" class="logo-item w-auto"
        /></a>
      </div>

      <section class="intro bg-white px-3 py-4 mx-auto mb-3 mt-4 shadow">
        <p class="text-center mb-0 mt-4">
          <a href="https://dicta.org.il">DICTA</a>, together with the DDR&amp;D
          / The Israeli National Program for NLP, are excited to release:
        </p>
        <h2 class="text-center my-3 font-weight-bold">
          Dicta-LM 3.0: Advancing The Frontier of Hebrew Sovereign LLMs 🔥
        </h2>
        <div class="blue-sep"></div>

        <div class="intro">
          <p>
            Dicta-LM 3.0 is a powerful open-weight collection of LLMs, trained
            on extensive corpora of Hebrew and English texts. The models are
            available for download and for unlimited use. The models set a new
            SOTA for their weight-class for Hebrew, both as base models and chat
            models.
          </p>
          <p>
            The models are available on HuggingFace, with an Apache 2.0 License.
            Both the base model and the designated chat models are available
            here:
            <a
              href="https://huggingface.co/collections/dicta-il/dictalm-30-collection"
              >DictaLM 3.0 collection</a
            >
          </p>
          <p>The models come in three sizes:</p>
          <ul>
            <li>
              <b>24B</b> - our flagship model, available as an efficient
              reasoning chat model, best-in-class for its size.
            </li>
            <li>
              <b>12B</b> - based on the Hybrid-SSM architecture, allowing long
              context with a minimal memory footprint, available as an instruct
              chat model.
            </li>
            <li>
              <b>1.7B</b> - an edge device model, available as both an instruct
              and reasoning chat model. This model is a tiny model that can run
              easily on consumer hardware.
            </li>
          </ul>

          <p>
            All 3 models have a native context length of 65k tokens (~26k Hebrew
            words), and support tool-calling - enabling integration with
            external tools and APIs.
          </p>
          <p class="alert alert-secondary mx-auto border small">
            <i class="fas fa-info-circle mr-2"></i>
            The models are available in full precision, fp8, and 4-bit
            precision. The 24B fits on a single H100 in full precision, and on a
            single L40S with FP8 precision.
            <br />
            <i class="fas fa-info-circle mr-2"></i>
            The 24B model will also be available as GGUF in various
            quantizations, to be used with any of Unsloth’s notebooks:
            <a
              href="https://huggingface.co/collections/unsloth/unsloth-dynamic-20-quants"
              >link</a
            >
          </p>
        </div>
      </section>

      <section class="features bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Training Details</h2>
        <div class="blue-sep"></div>
        <ul>
          <li>
            The models were initialized from strong open-weight base models: 24B
            from
            <a
              href="https://huggingface.co/mistralai/Mistral-Small-3.1-24B-Base-2503"
              target="_blank"
              >Mistral Small 3.1</a
            >, 12B from
            <a
              href="https://huggingface.co/nvidia/NVIDIA-Nemotron-Nano-12B-v2-Base"
              target="_blank"
              >NVIDIA Nemotron Nano V2</a
            >, and 1.7B from
            <a
              href="https://huggingface.co/Qwen/Qwen3-1.7B-Base"
              target="_blank"
              >Qwen3</a
            >.
          </li>
          <li>
            All three base models were continuously pretrained on ~150B tokens
            (75% Hebrew, 25% English), with a final native context length of 65k
            tokens.
          </li>
          <li>
            The models all went through rigorous post-training (SFT + RL) to
            produce high quality chat models, both instruct and reasoning
            models.
          </li>
          <li>
            All the chat models support tool-calling, enabling integration with
            external tools and APIs.
          </li>
          <li>
            The training was done using
            <a href="https://github.com/NVIDIA/NeMo" target="_blank"
              >NeMo Framework</a
            >
            and
            <a href="https://github.com/NVIDIA-NeMo/RL" target="_blank"
              >NeMo-RL</a
            >.
          </li>
        </ul>
        <p class="alert alert-info mx-auto border">
          <i class="fas fa-info-circle mr-2"></i>
          A full technical report detailing each stage of the training process
          is available for download
          <strong
            ><a
              href="/publications/DictaLM_3_0___Techincal_Report.pdf"
              target="_blank"
              >here</a
            ></strong
          >.
        </p>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Base Model Evaluation</h2>
        <div class="blue-sep"></div>
        <p>
          The models were evaluated on the tasks from the
          <a
            href="https://huggingface.co/spaces/hebrew-llm-leaderboard/leaderboard"
            >Hebrew-LLM-Leaderboard</a
          >. Most notably, our flagship (24B) model is currently the highest
          performing model on the Open Hebrew LLM Leaderboard of all models up
          to size 70B.
        </p>
        <p>
          Below are the per-model improvements from the original base model we
          started from, and after our continuous pretraining:
        </p>
        <div class="table-responsive">
          <table
            class="table table-sm table-striped table-hover text-center mx-auto"
          >
            <thead>
              <tr>
                <th>Model</th>
                <th>SNLI</th>
                <th>QA</th>
                <th>Sentiment</th>
                <th>Winograd</th>
                <th>Translation</th>
                <th>IL-Facts</th>
                <th>Average</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="8"><em>24B Parameter Models</em></td>
              </tr>
              <tr>
                <td>Mistral-Small-3.1-24B</td>
                <td>81.2</td>
                <td><b>77.9</b></td>
                <td>66.7</td>
                <td>80.9</td>
                <td>30.8</td>
                <td>58.5</td>
                <td>66.0</td>
              </tr>
              <tr>
                <td><b>DictaLM-3.0-24B</b></td>
                <td><b>86.0</b></td>
                <td>76.6</td>
                <td><b>68.7</b></td>
                <td><b>83.8</b></td>
                <td><b>37.2</b></td>
                <td><b>82.7</b></td>
                <td><b>72.5</b></td>
              </tr>
              <tr>
                <td><em>Improvement</em></td>
                <td class="text-success"><b>+4.8</b></td>
                <td>-1.3</td>
                <td class="text-success"><b>+2.0</b></td>
                <td class="text-success"><b>+2.9</b></td>
                <td class="text-success"><b>+6.4</b></td>
                <td class="text-success"><b>+24.2</b></td>
                <td class="text-success"><b>+6.5</b></td>
              </tr>
              <tr>
                <td colspan="8"><em>12B Parameter Models</em></td>
              </tr>
              <tr>
                <td>Nemotron-Nano-12B-v2</td>
                <td>71.4</td>
                <td>66.1</td>
                <td>64.5</td>
                <td>64.0</td>
                <td>21.5</td>
                <td>34.9</td>
                <td>53.7</td>
              </tr>
              <tr>
                <td><b>DictaLM-3.0-Nemotron-12B</b></td>
                <td><b>80.5</b></td>
                <td><b>77.8</b></td>
                <td><b>72.4</b></td>
                <td><b>79.5</b></td>
                <td><b>33.9</b></td>
                <td><b>54.8</b></td>
                <td><b>66.5</b></td>
              </tr>
              <tr>
                <td><em>Improvement</em></td>
                <td class="text-success"><b>+9.1</b></td>
                <td class="text-success"><b>+11.7</b></td>
                <td class="text-success"><b>+7.9</b></td>
                <td class="text-success"><b>+15.5</b></td>
                <td class="text-success"><b>+12.4</b></td>
                <td class="text-success"><b>+19.9</b></td>
                <td class="text-success"><b>+12.8</b></td>
              </tr>
              <tr>
                <td colspan="8"><em>1.7B Parameter Models</em></td>
              </tr>
              <tr>
                <td>Qwen3-1.7B-Base</td>
                <td>46</td>
                <td>61.4</td>
                <td>53</td>
                <td>54.3</td>
                <td>20.1</td>
                <td>24.9</td>
                <td>43.3</td>
              </tr>
              <tr>
                <td><b>DictaLM-3.0-1.7B</b></td>
                <td><b>67.1</b></td>
                <td><b>62.8</b></td>
                <td><b>61.9</b></td>
                <td><b>57.2</b></td>
                <td><b>29.2</b></td>
                <td><b>30.6</b></td>
                <td><b>51.5</b></td>
              </tr>
              <tr>
                <td><em>Improvement</em></td>
                <td class="text-success"><b>+21.1</b></td>
                <td class="text-success"><b>+1.4</b></td>
                <td class="text-success"><b>+8.9</b></td>
                <td class="text-success"><b>+2.9</b></td>
                <td class="text-success"><b>+9.1</b></td>
                <td class="text-success"><b>+5.7</b></td>
                <td class="text-success"><b>+8.2</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">
          Chat Model Evaluation - Hebrew
        </h2>
        <div class="blue-sep"></div>
        <p>
          We evaluated the model on 5 tasks, based on the new
          <a
            href="https://huggingface.co/spaces/hebrew-llm-leaderboard/chat-leaderboard"
            >Hebrew LLM Chat Leaderboard</a
          >. Our models excel particularly in tasks requiring deep understanding
          of Hebrew morphology and semantics, such as diacritization (Nikud) and
          summarization.
        </p>
        <div class="table-responsive">
          <table
            class="table table-sm table-striped table-hover text-center mx-auto"
          >
            <thead>
              <tr>
                <th>Model</th>
                <th>Summarization</th>
                <th>Translation</th>
                <th>Winograd</th>
                <th>Trivia</th>
                <th>Nikud</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>DictaLM-3.0-24B-Thinking</b></td>
                <td><b>56.86</b></td>
                <td><b>30.09</b></td>
                <td>78.06</td>
                <td><b>60.13</b></td>
                <td><b>86.86</b></td>
              </tr>
              <tr>
                <td>gemma3-27B-it</td>
                <td>44.54</td>
                <td>26.73</td>
                <td><b>79.86</b></td>
                <td>45.51</td>
                <td>60.21</td>
              </tr>
              <tr>
                <td>aya-expanse-32B</td>
                <td>29.46</td>
                <td>17.10</td>
                <td>80.58</td>
                <td>53.82</td>
                <td>45.40</td>
              </tr>
              <tr>
                <td>Llama-3.3-70B-Instruct</td>
                <td>37.83</td>
                <td>19.31</td>
                <td>83.45</td>
                <td><b>60.13</b></td>
                <td>4.05</td>
              </tr>
              <tr>
                <td colspan="6"><b>Smaller models (~12B)</b></td>
              </tr>
              <tr>
                <td><b>DictaLM-3.0-Nemotron-12B-Instruct</b></td>
                <td>33.27</td>
                <td>13.50</td>
                <td>73.74</td>
                <td><b>45.18</b></td>
                <td><b>76.12</b></td>
              </tr>
              <tr>
                <td>gemma-3-12b-it</td>
                <td><b>39.48</b></td>
                <td><b>16.50</b></td>
                <td><b>75.90</b></td>
                <td>44.85</td>
                <td>51.78</td>
              </tr>
              <tr>
                <td>Qwen3-14B (think)</td>
                <td>15.83</td>
                <td>0.90</td>
                <td>73.38</td>
                <td>41.86</td>
                <td>4.73</td>
              </tr>
              <tr>
                <td colspan="6"><b>Tiny models (~1.7B)</b></td>
              </tr>
              <tr>
                <td>DictaLM-3.0-1.7B-Instruct</td>
                <td>9.72</td>
                <td>2.16</td>
                <td><b>58.2</b></td>
                <td>30.21</td>
                <td><b>52.76</b></td>
              </tr>
              <tr>
                <td><b>DictaLM-3.0-1.7B-Thinking</b></td>
                <td><b>10.22</b></td>
                <td><b>2.51</b></td>
                <td>55.76</td>
                <td>31.23</td>
                <td>47.2</td>
              </tr>
              <tr>
                <td>gemma-3-1b-it</td>
                <td>0.35</td>
                <td>0.15</td>
                <td>47.84</td>
                <td>26.58</td>
                <td>3.44</td>
              </tr>
              <tr>
                <td>Qwen3-1.7B (think)</td>
                <td>0.4</td>
                <td>0</td>
                <td>51.08</td>
                <td>21.59</td>
                <td>2.93</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">
          Chat Model Evaluation - English
        </h2>
        <div class="blue-sep"></div>
        <p>
          We also evaluate the model’s performance on a variety of English
          tasks, to test whether the original English capabilities were
          maintained despite the intense Hebrew continuous pre-training. The
          results for the flagship 24B model are displayed here:
        </p>
        <div class="table-responsive">
          <table
            class="table table-sm table-striped table-hover text-center mx-auto"
          >
            <thead>
              <tr>
                <th>Category</th>
                <th>Benchmark</th>
                <th>DictaLM 3.0 24B-Think</th>
                <th>Gemma 3 27B</th>
                <th>Mistral Small 3.1</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowspan="2">Math</td>
                <td>MATH</td>
                <td>86.41</td>
                <td><b>87.40</b></td>
                <td>67.93</td>
              </tr>
              <tr>
                <td>OMEGA</td>
                <td><b>28.38</b></td>
                <td>24.00</td>
                <td>13.76</td>
              </tr>
              <tr>
                <td rowspan="3">Reasoning</td>
                <td>BigBenchHard</td>
                <td>73.00</td>
                <td><b>82.40</b></td>
                <td>71.91</td>
              </tr>
              <tr>
                <td>ZebraLogic</td>
                <td><b>47.80</b></td>
                <td>24.80</td>
                <td>23.10</td>
              </tr>
              <tr>
                <td>AGI Eval (EN)</td>
                <td><b>82.93</b></td>
                <td>76.90</td>
                <td>75.87</td>
              </tr>
              <tr>
                <td>Inst. Following</td>
                <td>IFEval</td>
                <td><b>88.17</b></td>
                <td>85.40</td>
                <td>79.48</td>
              </tr>
              <tr>
                <td rowspan="3">Knowledge</td>
                <td>MMLU</td>
                <td><b>85.93</b></td>
                <td>74.60</td>
                <td>83.11</td>
              </tr>
              <tr>
                <td>PopQA</td>
                <td><b>30.59</b></td>
                <td>30.20</td>
                <td>29.70</td>
              </tr>
              <tr>
                <td>GPQA</td>
                <td><b>55.13</b></td>
                <td>45.00</td>
                <td>44.87</td>
              </tr>
              <tr>
                <td>Chat</td>
                <td>AlpacaEval 2 LC</td>
                <td><b>74.11</b></td>
                <td>65.50</td>
                <td>41.22</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          The models were evaluated on the Olmo3 evaluation set, using the
          <a href="https://github.com/allenai/olmes">Olmes</a> library*
        </p>
        <p class="small">*See the technical report for details.</p>
      </section>

      <section class="bg-white px-3 py-4 mx-auto mb-3 shadow">
        <h2 class="text-center font-weight-bold">Summary</h2>
        <div class="blue-sep"></div>
        <p>
          We are excited to release the DictaLM 3.0 family to the world - a new
          collection of sovereign generative LLMs specializing in Hebrew - under
          a permissive license (for both commercial &amp; academic use).
        </p>
        <p class="text-center">
          We are proud to advance NLP capabilities in Hebrew, and are happy to
          have the opportunity to share our products with the community.
        </p>
      </section>

      <div
        class="alert alert-primary mx-auto border border-primary text-center"
      >
        <p class="mb-0">
          Special thanks to the <b>NVIDIA DGX Cloud Lepton team</b> for
          providing compute resources and support. Gratitude to Noel Osagie,
          Greg Kosiorowski, Martin Piercy, and Anshul Jindal.
        </p>
        <hr />
        <p class="mb-0">
          We also thank the <b>NVIDIA team</b> for their expertise and
          collaboration: Adam Henryk Grzywaczewski, Oleg Sudakov, Meriem
          Bendris, Ofir Zamir, Asher Fredman, Natalia Segal, Amit Bleiwess,
          Sergio Perez, and Liana Mikaelyan.
        </p>
        <hr />
        <p class="mb-0">
          Released by DICTA, in cooperation with the DDR&amp;D / The Israeli
          National Program for NLP.
          <br />
          For questions, please contact Shaltiel Shmidman, Lead Programmer at
          the Deep Learning Lab at DICTA, at
          <a href="mailto:shaltiel@dicta.org.il">shaltiel@dicta.org.il</a>
        </p>
      </div>
    </b-container>
  </div>
</template>

<script>
export default {
  name: "DictaLM3_0",
  computed: {
    hebrew() {
      return this.$settings.hebrew
    },
  },
}
</script>

<style scoped lang="scss">
.logo-item {
  max-height: 100px;
}
li {
  margin-bottom: 10px;
}
.alert,
section {
  border-radius: 8px;
}
@media (min-width: 992px) {
  .developer-information {
    table,
    .alert,
    section {
      max-width: 85%;
      img.table-img {
        max-width: 75% !important;
      }
    }
  }
}
</style>
